import React, { useEffect, useState } from 'react'
import { fetchTeams, joinTeam, Team } from '../../../services/api'
import { ReviewMode, setStoredMode, setStoredTeamName } from '../../../services/user'

export function ModeGate({ userEmail, onSubmit }: {
  userEmail: string
  onSubmit: (mode: ReviewMode, teamName: string | null) => void
}) {
  const [mode, setMode] = useState<ReviewMode | null>(null)
  const [teams, setTeams] = useState<Team[]>([])
  const [teamInput, setTeamInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchTeams().then(setTeams).catch(() => {})
  }, [])

  async function handleTeamSubmit(name: string) {
    const trimmed = name.trim()
    if (!trimmed) { setError('Please enter a team name'); return }
    setSaving(true)
    try {
      await joinTeam(userEmail, trimmed)
      setStoredMode('team')
      setStoredTeamName(trimmed)
      onSubmit('team', trimmed)
    } catch {
      setError('Failed to join team')
    } finally {
      setSaving(false)
    }
  }

  function handleIndividual() {
    setStoredMode('individual')
    onSubmit('individual', null)
  }

  if (mode === 'team') {
    return (
      <div className="bg-an-surface border border-an-line-mid rounded-xl px-5 py-4 animate-[an-in_0.15s_ease]">
        <div className="text-[13px] font-semibold text-an-ink tracking-[-0.01em] mb-1">Join a team</div>
        <div className="text-[11.5px] text-an-ink-3 mb-3">
          Type a new team name to create one, or select an existing team.
        </div>

        {teams.length > 0 && (
          <div className="flex flex-wrap gap-[5px] mb-3">
            {teams.map(t => (
              <button
                key={t.team_name}
                onClick={() => handleTeamSubmit(t.team_name)}
                disabled={saving}
                className="px-3 py-[6px] rounded-lg border border-an-line-mid bg-an-surface-2 text-an-ink text-[12.5px] cursor-pointer hover:border-an-accent hover:text-an-accent transition-colors"
              >
                {t.team_name}
                <span className="text-an-ink-3 ml-1.5 text-[11px]">{t.member_count}</span>
              </button>
            ))}
          </div>
        )}

        <form onSubmit={e => { e.preventDefault(); handleTeamSubmit(teamInput) }} className="flex items-center gap-2">
          <div className="flex flex-col flex-1">
            <input
              type="text"
              value={teamInput}
              onChange={e => { setTeamInput(e.target.value); setError('') }}
              placeholder="Enter team name"
              className="px-3 py-[7px] rounded-lg border border-an-line-mid bg-an-surface-2 text-an-ink text-[13px] w-full outline-none focus:border-an-accent transition-colors"
            />
            {error && <span className="text-[11px] text-an-red mt-1">{error}</span>}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-[7px] rounded-lg border-none bg-an-accent text-white text-[13px] font-semibold cursor-pointer shrink-0"
            style={{ opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Joining...' : 'Join'}
          </button>
        </form>

        <button
          onClick={() => setMode(null)}
          className="mt-2.5 text-[11.5px] text-an-ink-3 bg-transparent border-none cursor-pointer p-0 hover:text-an-ink transition-colors"
        >
          &larr; Back
        </button>
      </div>
    )
  }

  return (
    <div className="bg-an-surface border border-an-line-mid rounded-xl px-5 py-4 animate-[an-in_0.15s_ease]">
      <div className="text-[13px] font-semibold text-an-ink tracking-[-0.01em] mb-0.5">How would you like to track reviews?</div>
      <div className="text-[11.5px] text-an-ink-3 mb-3.5">You can switch between these at any time.</div>

      <div className="flex gap-2.5">
        <button
          onClick={handleIndividual}
          className="flex-1 flex flex-col items-start px-4 py-3.5 rounded-lg border border-an-line-mid bg-an-surface-2 cursor-pointer hover:border-an-accent transition-colors text-left"
        >
          <div className="text-[13px] font-semibold text-an-ink mb-0.5">Individual</div>
          <div className="text-[11.5px] text-an-ink-3 leading-snug">Your reviews are private to you.</div>
        </button>
        <button
          onClick={() => setMode('team')}
          className="flex-1 flex flex-col items-start px-4 py-3.5 rounded-lg border border-an-line-mid bg-an-surface-2 cursor-pointer hover:border-an-accent transition-colors text-left"
        >
          <div className="text-[13px] font-semibold text-an-ink mb-0.5">Team</div>
          <div className="text-[11.5px] text-an-ink-3 leading-snug">Share reviews across your team.</div>
        </button>
      </div>
    </div>
  )
}
